import {
  Document,
  Page,
  View,
  Image,
  Text,
  StyleSheet,
} from "@react-pdf/renderer";
import React, { ReactNode } from "react";
import logo from "@packages/assets/logo.png";
import { useTranslation } from "react-i18next";
import { CourseData } from "@packages/constant/CourseData";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize, FontSizePreset } from "./fontSizeAdjustment";
import { format } from "date-fns";
import { tr, enUS } from "date-fns/locale";
// Types for PDF document properties
type PageMode =
  | "useNone"
  | "useOutlines"
  | "useThumbs"
  | "fullScreen"
  | "useOC"
  | "useAttachments";

type PageLayout =
  | "singlePage"
  | "oneColumn"
  | "twoColumnLeft"
  | "twoColumnRight"
  | "twoPageLeft"
  | "twoPageRight";

interface DocumentMetadata {
  title?: string;
  author?: string;
  subject?: string;
  keywords?: string;
  creator?: string;
  producer?: string;
  pdfVersion?: string;
  language?: string;
  pageMode?: PageMode;
  pageLayout?: PageLayout;
  orientation?: "portrait" | "landscape";
}

interface PdfLayoutProps extends DocumentMetadata {
  courseName?: string;
  gradeLevel?: string | number;
  topic?: string;
  imageUrl?: string;
  headerTextColor?: string;
  children: ReactNode;
  customizationSettings?: PdfCustomizationSettings;
  createdDate?: string;
}

interface PageHeaderProps {
  courseName?: string;
  gradeLevel?: string | number;
  topic?: string;
  headerTextColor?: string;
  isFirstPage?: boolean;
  customizationSettings?: PdfCustomizationSettings;
  createdDate?: string;
  teacherName?: string;
}

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "Montserrat",
    backgroundColor: "#ffffff",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    marginBottom: 15,
    backgroundColor: "#f3f4f6",
    padding: 8,
    borderRadius: 6,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  repeatedHeader: {
    marginTop: 20,
    marginBottom: 15,
  },
  headerTop: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    marginBottom: 8,
  },
  headerBottom: {
    display: "flex",
    alignItems: "center",
  },
  divider: {
    height: 20,
    width: 1,
    backgroundColor: "#e2e8f0",
    marginHorizontal: 10,
  },
  imageContainer: {
    marginBottom: 10,
    alignItems: "center",
  },
  image: {
    width: "80%",
    height: 180,
    objectFit: "cover",
    borderRadius: 8,
  },
  logo: {
    width: 64,
    height: 64,
    marginBottom: 15,
  },
  logoContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    marginBottom: 15,
    justifyContent: "flex-start", // Default to left alignment
  },
  logoContainerCenter: {
    justifyContent: "center",
  },
  logoContainerRight: {
    justifyContent: "flex-end",
  },
  contentContainer: {
    flex: 1,
  },
  headerText: {
    fontSize: 12,
  },
  courseText: {
    fontSize: 12,
    marginHorizontal: 8,
  },
  topicText: {
    fontSize: 12,
    textAlign: "center",
  },

  teacherDateText: {
    fontSize: 10,
    color: "#4b5563",
  },
});

const PageHeader: React.FC<PageHeaderProps> = ({
  courseName,
  gradeLevel,
  topic,
  headerTextColor = "#1f2937",
  isFirstPage = false,
  customizationSettings,
  createdDate,
  teacherName,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "tr" ? tr : enUS;
  const getLogoContainerStyle = () => {
    if (!customizationSettings?.madlenLogoPosition) return styles.logoContainer;

    switch (customizationSettings.madlenLogoPosition) {
      case "center":
        return [styles.logoContainer, styles.logoContainerCenter];
      case "topRight":
        return [styles.logoContainer, styles.logoContainerRight];
      case "topLeft":
      default:
        return styles.logoContainer;
    }
  };

  const fontSizePreset = customizationSettings?.fontSize;

  return (
    <View
      style={[styles.header, ...(isFirstPage ? [] : [styles.repeatedHeader])]}
    >
      {isFirstPage && (
        <View style={getLogoContainerStyle()}>
          <Image src={logo} style={styles.logo} />
        </View>
      )}

      <View style={styles.headerTop}>
        {courseName && (
          <Text
            style={[
              styles.courseText,
              {
                color: headerTextColor,
                fontSize: scaleFontSize(
                  styles.courseText.fontSize,
                  fontSizePreset,
                ),
              },
            ]}
          >
            {t(
              `courseType.${CourseData[
                courseName as keyof typeof CourseData
              ]?.name(Number(gradeLevel ?? 8))}`,
            )}
          </Text>
        )}
        {courseName && gradeLevel && <View style={styles.divider} />}
        {gradeLevel && (
          <Text
            style={[
              styles.courseText,
              {
                color: headerTextColor,
                fontSize: scaleFontSize(
                  styles.courseText.fontSize,
                  fontSizePreset,
                ),
              },
            ]}
          >
            {t("hooks.form.gradeOf", {
              grade: gradeLevel,
            })}
          </Text>
        )}
      </View>
      {topic && (
        <View style={styles.headerBottom}>
          <Text
            style={[
              styles.topicText,
              {
                color: headerTextColor,
                fontSize: scaleFontSize(
                  styles.topicText.fontSize,
                  fontSizePreset as FontSizePreset,
                ),
              },
            ]}
          >
            {topic}
          </Text>
        </View>
      )}
      {/* Teacher name and date section */}
      {isFirstPage &&
        (customizationSettings?.showTeacherName ||
          customizationSettings?.showDate) && (
          <View style={[styles.headerTop, { marginBottom: 0, marginTop: 8 }]}>
            {customizationSettings?.showTeacherName && teacherName && (
              <Text
                style={[
                  styles.teacherDateText,
                  {
                    fontSize: scaleFontSize(
                      styles.teacherDateText.fontSize,
                      fontSizePreset as FontSizePreset,
                    ),
                  },
                ]}
              >
                {teacherName}
              </Text>
            )}
            {customizationSettings?.showTeacherName &&
              customizationSettings?.showDate && (
                <View style={styles.divider} />
              )}

            {customizationSettings?.showDate && createdDate && (
              <Text
                style={[
                  styles.teacherDateText,
                  {
                    fontSize: scaleFontSize(
                      styles.teacherDateText.fontSize,
                      fontSizePreset as FontSizePreset,
                    ),
                  },
                ]}
              >
                {format(new Date(createdDate), "dd MMMM yyyy", {
                  locale,
                })}
              </Text>
            )}
          </View>
        )}
    </View>
  );
};

export const PdfLayout: React.FC<PdfLayoutProps> = ({
  courseName,
  gradeLevel,
  topic,
  imageUrl,
  headerTextColor = "#1f2937",
  children,
  title,
  orientation = "portrait",
  author,
  subject,
  keywords,
  creator,
  producer,
  language,
  createdDate,
  customizationSettings,
}) => {
  return (
    <Document
      title={title || topic}
      author={author}
      subject={subject}
      keywords={keywords}
      creator={creator}
      producer={producer}
      language={language}
    >
      <Page
        size="A4"
        orientation={orientation}
        style={styles.page}
        wrap={orientation === "portrait"}
      >
        <PageHeader
          courseName={courseName}
          gradeLevel={gradeLevel}
          topic={topic}
          headerTextColor={headerTextColor}
          isFirstPage={true}
          customizationSettings={customizationSettings}
          createdDate={createdDate}
          teacherName={author}
        />
        {imageUrl && customizationSettings?.showGeneratedImage && (
          <View style={styles.imageContainer}>
            <Image src={imageUrl} style={styles.image} />
          </View>
        )}
        <View style={styles.contentContainer} wrap>
          {children}
        </View>
      </Page>
    </Document>
  );
};

export default PdfLayout;
